import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  Chip,
  Dialog,
  DialogContent
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import MultipleImageUploadDialog from "../../../components/ImgUpload/MultipleImageUpload";
import { ProviderServices } from '../CrudServices';
import { UserProfileContext } from '../UserProfile';
import { Helpers } from '../../../services/Helpers';
import Swal from 'sweetalert2';
import { useFormContext } from 'react-hook-form';
import GalleryView from '../../../components/ImageViewer/GalleryView';

const MAX_IMAGES = 15; // Maximum number of images allowed

const PersonalGallery = () => {
  const { providerPersonalDetails, setProviderPersonalDetails } = useContext(UserProfileContext);
  const { setValue } = useFormContext();

  const [imgModalOpen, setImgModalOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const imgData = {
    path: "expert-gallery",
    img_type: "single"
  };

  useEffect(() => {
    const initialGallery = (providerPersonalDetails?.gallery || []).map(img => ({
      name: img,
      isNew: false
    }));
    setGalleryImages(initialGallery);
  }, [providerPersonalDetails?.gallery]);

  const closeModal = () => setImgModalOpen(false);

  const handleImageUpload = (uploadedImageData) => {
    const newImgs = uploadedImageData.map(img => ({
      name: img.imageName,
      isNew: true
    }));
    const updatedGallery = [...galleryImages, ...newImgs];
    setGalleryImages(updatedGallery);
    setValue('gallery', updatedGallery.map(img => img.name), { shouldDirty: true });
  };

  const handleDeleteImage = (imgToDelete) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will remove the image from the gallery.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then(result => {
      if (result.isConfirmed) {
        const updatedGallery = galleryImages.filter(img => img.name !== imgToDelete);
        setGalleryImages(updatedGallery);
        setValue('gallery', updatedGallery.map(img => img.name), { shouldDirty: true });
        Swal.fire('Deleted!', '', 'success');
      }
    });
  };

  const openViewer = (index) => {
    setActiveIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  const handleAddPhotosClick = () => {
    if (galleryImages.length >= MAX_IMAGES) {
      Swal.fire({
        title: 'Maximum limit reached',
        text: `You can only have a maximum of ${MAX_IMAGES} images in your gallery.`,
        icon: 'warning'
      });
      return;
    }
    setImgModalOpen(true);
  };

  const getRemainingSlots = () => MAX_IMAGES - galleryImages.length;

  return (
    <>
      {/* Upload Dialog */}
      <MultipleImageUploadDialog
        open={imgModalOpen}
        onClose={closeModal}
        imgData={imgData}
        imgUploadResponse={handleImageUpload}
        MAX_IMAGES ={ getRemainingSlots()}
      />

      {/* Image Viewer Modal */}
      <Dialog open={viewerOpen} maxWidth="md" fullWidth>
        <Box sx={{ p: '.5% 0% .5% 2%', border: '2px solid lightGray', bgcolor: 'lightGray',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
            <Typography sx={{ fontWeight: '600', fontSize: '.85rem' }}>Image Viewer</Typography>
            <IconButton sx={{bgcolor:'#ec0f0f',borderRadius:0,p:0,'&:hover':{bgcolor:'red'}}} onClick={closeViewer}>
                <CloseIcon sx={{color:'#fff'}}/>
            </IconButton>
        </Box>
        {/* <DialogContent sx={{ position: 'relative', p: 1, textAlign: 'center' }}> */}
          <GalleryView images={galleryImages} selectedIndex={activeIndex} baseUrl={`${Helpers.ImagePathUrl()}/expert-gallery`}/>
        {/* </DialogContent> */}
      </Dialog>

      {/* Gallery Header with Image Count */}
            <Box sx={{ mx: 'auto', my: '1%', px: '2%' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Images: {galleryImages.length}/{MAX_IMAGES} {getRemainingSlots() > 0 && `(${getRemainingSlots()} slots remaining)`}
              </Typography>
            </Box>

      {/* Image Gallery Grid */}
      <Grid container sx={{ mx: 'auto', my: '1%' }}>
        <Grid item xs={12} sx={{ display: 'flex', gap: '2%', flexWrap: 'wrap', p: '2%' }}>
          {galleryImages.map((item, index) => (
            <Box
              key={item.name}
              sx={{ position: 'relative', width: '18%', mb: '2%', cursor: 'pointer' }}
            >
              <img
                src={`${Helpers.ImagePathUrl()}/expert-gallery/md/${item.name}`}
                alt="expert-gallery"
                style={{ width: 100, height: 100, border: '1px solid lightGray', borderRadius: '1rem' }}
                onClick={() => openViewer(index)}
              />

              {item.isNew && (
                <Chip
                  label="New"
                  // color="success"
                  size="small"
                  sx={{ fontSize: '0.5rem', height: 'fit-content', '& .MuiChip-icon': { fontSize: '0.65rem' },position: 'absolute', top: 5, left: 5,bgcolor:'#3a2bde',color:'#fff'}}
                />
              )}

              <IconButton
                size="small"
                onClick={() => handleDeleteImage(item.name)}
                sx={{ position: 'absolute', top: 5, right: 5, backgroundColor: '#fff',p:0 }}
              >
                <DeleteIcon fontSize="small" sx={{color:'#c70000',fontSize:'1rem'}}/>
              </IconButton>
            </Box>
          ))}

          {/* Add Image Box */}
          <Box
            sx={{
              width: '18%',
              height: 100,
              cursor: 'pointer',
              display: galleryImages.length >= MAX_IMAGES ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3%',
              border: '2px dashed gray',
              borderRadius: '2rem'
            }}
            onClick={handleAddPhotosClick}
          >
            <AddIcon />
            <Typography sx={{ textAlign: 'center' }}>Add Photos</Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default PersonalGallery;
